import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const THEME_STORAGE_KEY = "oneview-theme";

/**
 * @typedef {"light" | "dark"} ThemeMode
 */

/**
 * @typedef {Object} ThemeContextValue
 * @property {ThemeMode} theme - Current theme mode
 * @property {(theme: ThemeMode) => void} setTheme - Set theme and persist
 * @property {() => void} toggleTheme - Toggle between light and dark
 */

const ThemeContext = createContext(/** @type {ThemeContextValue | null} */ (null));

/**
 * Read stored theme preference from localStorage.
 * @returns {ThemeMode}
 */
function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // ignore storage errors
  }
  return "light";
}

/**
 * Apply theme class and native color-scheme on the document root.
 * color-scheme keeps OS date/time pickers in sync with the app theme.
 * @param {ThemeMode} theme
 */
function applyThemeClass(theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.style.colorScheme = theme;
}

/**
 * Theme provider — persists light/dark preference and syncs the `dark` class.
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(/** @type {ThemeMode} */ (readStoredTheme));

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const setTheme = useCallback((/** @type {ThemeMode} */ next) => {
    setThemeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // ignore storage errors
    }
    applyThemeClass(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [setTheme, theme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * Access theme context. Throws outside ThemeProvider.
 * @returns {ThemeContextValue}
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}

export { THEME_STORAGE_KEY };
